import { routes } from '@/config/routes'
import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { cn } from '@/utils/cn'
import { viemChains } from '@vaultsfyi/common/constants'
import { generatePath, useNavigate } from 'react-router'
import { type Address, isAddressEqual } from 'viem'
import { useEnsName } from 'wagmi'
import { Button } from '../common/button'
import { useConnectWalletModalContext } from '../connect/connectModal'
import { EditTrackableAddressDialog } from './editTrackableAddressDialog'

const { mainnet } = viemChains

interface Props {
  address: Address
  className?: string
}

export const TrackedAddressEditor = ({ address, className }: Props) => {
  const navigate = useNavigate()
  const { account } = useAccountContext()
  const { trackEvent } = useMixpanel()
  const { setOpenModal } = useConnectWalletModalContext()
  const { data: ensName } = useEnsName({ address, chainId: mainnet.id })
  const renderedAddress = ensName ?? shortenAddress(address)

  function handleConnectWallet() {
    setOpenModal('connect')
    trackEvent('Address editor connect wallet button clicked')
  }

  function handleUseWallet() {
    trackEvent('Track wallet', { Method: 'address' })
    navigate(generatePath(routes.dashboard, { address: '' }))
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border bg-surface p-5 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl">
          <img src={`https://effigy.im/a/${address}.svg`} alt={renderedAddress} className="size-full " />
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-medium text-heading-foreground text-sm">Tracking wallet {renderedAddress}</p>
          <div className="flex items-center gap-1.5">
            <div
              className={cn(
                'relative ml-1 size-2 rounded-full bg-success',
                'before:absolute before:h-full before:w-full before:animate-ping before:rounded-full before:bg-success'
              )}
            />
            <span className="font-medium text-success text-xs">Live data</span>
          </div>
        </div>
      </div>
      <div className="flex xs:flex-row flex-col items-center gap-x-2 gap-y-0.5">
        <EditTrackableAddressDialog address={address} className="flex-grow max-xs:w-full" />
        {!account && (
          <>
            <span className="text-body-foreground text-xs">or</span>
            <Button variant="primary" size="sm" onClick={handleConnectWallet} className="flex-grow max-xs:w-full">
              Connect wallet
            </Button>
          </>
        )}
        {account && !isAddressEqual(address, account) && (
          <>
            <span className="text-body-foreground text-xs">or</span>
            <Button variant="primary" size="sm" onClick={handleUseWallet} className="flex-grow max-xs:w-full">
              Use your wallet
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
